export interface RequestDedupOptions {
  enableDedup?: boolean;
  bypassCache?: boolean;
  ttlMs?: number;
}

interface RequestDeduperOptions<P> {
  defaultTtlMs: number;
  keyBuilder?: (params: P) => string;
}

interface CacheEntry<R> {
  expiresAt: number;
  value: R;
}

function normalizeForKey(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(normalizeForKey);
  }

  if (value && typeof value === 'object') {
    const sortedEntries = Object.entries(value as Record<string, unknown>).sort(
      ([a], [b]) => a.localeCompare(b)
    );

    const normalized: Record<string, unknown> = {};
    sortedEntries.forEach(([key, entryValue]) => {
      if (entryValue !== undefined) {
        normalized[key] = normalizeForKey(entryValue);
      }
    });

    return normalized;
  }

  return value;
}

function buildStableKey(params: unknown): string {
  if (params === undefined) {
    return '__undefined__';
  }

  if (params === null) {
    return '__null__';
  }

  return JSON.stringify(normalizeForKey(params));
}

export function createRequestDeduper<P, R>(
  fetcher: (params: P) => Promise<R>,
  options: RequestDeduperOptions<P>
): (params: P, requestOptions?: RequestDedupOptions) => Promise<R> {
  const inFlight = new Map<string, Promise<R>>();
  const cache = new Map<string, CacheEntry<R>>();
  const latestRequestSeq = new Map<string, number>();

  const keyBuilder = options.keyBuilder || ((params: P) => buildStableKey(params));

  function clearExpiredCache(now: number) {
    cache.forEach((entry, key) => {
      if (entry.expiresAt <= now) {
        cache.delete(key);
      }
    });
  }

  return async (params: P, requestOptions: RequestDedupOptions = {}) => {
    const enableDedup = requestOptions.enableDedup === true;
    if (!enableDedup) {
      return fetcher(params);
    }

    const key = keyBuilder(params);
    const ttlMs = requestOptions.ttlMs ?? options.defaultTtlMs;
    const bypassCache = requestOptions.bypassCache === true;

    const now = Date.now();
    clearExpiredCache(now);

    if (!bypassCache) {
      const cached = cache.get(key);
      if (cached && cached.expiresAt > now) {
        return cached.value;
      }

      const inFlightRequest = inFlight.get(key);
      if (inFlightRequest) {
        return inFlightRequest;
      }
    }

    const requestSeq = (latestRequestSeq.get(key) ?? 0) + 1;
    latestRequestSeq.set(key, requestSeq);

    const requestPromise = fetcher(params)
      .then((result) => {
        if (latestRequestSeq.get(key) === requestSeq) {
          cache.set(key, {
            value: result,
            expiresAt: Date.now() + ttlMs,
          });
        }

        return result;
      })
      .finally(() => {
        if (inFlight.get(key) === requestPromise) {
          inFlight.delete(key);
        }
      });

    if (!bypassCache) {
      inFlight.set(key, requestPromise);
    }

    return requestPromise;
  };
}
